// Detailed tracing of every model interaction: prompt/response sizes, latency,
// HTTP retries, parse outcomes. A ring buffer persists across reloads and is
// exportable, so real usage sessions double as benchmark data.

import { storage } from "../lib/storage";

export interface TraceEvent {
  ts: number;                 // epoch ms at request start
  kind: string;               // "nav.propose" | "grounding" | "constraints" | "doc.route" | "judge" | "decompose" | "whatif.compare" | "synthesis" | ...
  stream?: boolean;
  priority?: number;
  promptChars: number;
  outputChars?: number;
  latencyMs?: number;         // full call latency (incl. queue + retries)
  ttftMs?: number;            // streaming: time to first token
  ok: boolean;
  httpRetries?: number;       // 429/503 rounds before success
  error?: string;
  parse?: "ok" | "repaired" | "salvaged" | "failed";  // set by callers that parse JSON
  sessionId?: string;
  meta?: Record<string, unknown>;
}

const LS_KEY = "lattice.trace.v1";
const MAX_EVENTS = 600;

let events: TraceEvent[] = load();
let persistTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<(e: TraceEvent) => void>();

function load(): TraceEvent[] {
  try { return JSON.parse(storage.get(LS_KEY) || "[]"); } catch { return []; }
}

function schedulePersist() {
  if (persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    storage.set(LS_KEY, JSON.stringify(events));
  }, 800);
}

export function trace(e: TraceEvent): void {
  events.push(e);
  if (events.length > MAX_EVENTS) events = events.slice(-MAX_EVENTS);
  listeners.forEach((fn) => fn(e));
  schedulePersist();
}

/** Patch the most recent event matching a predicate (e.g. attach parse outcome). */
export function amendLastTrace(kind: string, patch: Partial<TraceEvent>): void {
  for (let i = events.length - 1; i >= 0; i--) {
    if (events[i]!.kind === kind) { Object.assign(events[i]!, patch); schedulePersist(); return; }
  }
}

/**
 * Attach a parse outcome to the last call of this kind; on failure, keep the
 * head of the raw output so failure modes can be diagnosed from traces alone.
 */
export function traceParseOutcome(kind: string, parse: "ok" | "repaired" | "failed", raw?: string): void {
  amendLastTrace(kind, parse === "failed" && raw != null
    ? { parse, meta: { rawHead: raw.slice(0, 1200) } }
    : { parse });
}

export function onTrace(fn: (e: TraceEvent) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getTrace(): readonly TraceEvent[] { return events; }

export function clearTrace(): void {
  events = [];
  storage.remove(LS_KEY);
}

export function exportTrace(): string { return JSON.stringify(events, null, 2); }

/** Aggregate stats over the buffer — the quick health readout. */
export function traceStats(since = 0) {
  const evs = events.filter((e) => e.ts >= since);
  const lat = evs.filter((e) => e.ok && e.latencyMs != null).map((e) => e.latencyMs!).sort((a, b) => a - b);
  const pct = (p: number) => (lat.length ? lat[Math.min(lat.length - 1, Math.floor(p * lat.length))] : null);
  const byKind: Record<string, { n: number; errors: number; parseFailed: number; repaired: number }> = {};
  for (const e of evs) {
    const k = (byKind[e.kind] ||= { n: 0, errors: 0, parseFailed: 0, repaired: 0 });
    k.n++;
    if (!e.ok) k.errors++;
    if (e.parse === "failed") k.parseFailed++;
    if (e.parse === "repaired" || e.parse === "salvaged") k.repaired++;
  }
  return {
    calls: evs.length,
    errors: evs.filter((e) => !e.ok).length,
    p50: pct(0.5), p90: pct(0.9), p99: pct(0.99),
    retriedCalls: evs.filter((e) => (e.httpRetries || 0) > 0).length,
    byKind,
  };
}
